import { createFileRoute, Outlet } from '@tanstack/react-router'
import { bootstrapAuth } from '@/features/auth/bootstrap'
import { Shell } from '@/components/layout/Shell'

/**
 * Pathless layout-route — auth gate for everything under `_authenticated/`.
 *
 * Story E01.07 introduced this guard as a synchronous presence-check on
 * `useAuthStore.isAuthenticated`. That worked for in-SPA navigation, but
 * not for F5 / direct URL paste — the auth-store doesn't persist (so the
 * httpOnly cookie was the only surviving evidence of a live session).
 *
 * Story E02.05 replaces the inline check with `bootstrapAuth(pathname)`,
 * a singleflight loader that:
 *   - fast-paths when the in-memory store says we're already authenticated,
 *   - probes `/api/workspaces` on a cold mount to verify the cookie,
 *   - hydrates `useAuthStore` + `useWorkspaceStore` on success,
 *   - throws `redirect({ to: '/login', ... })` on 401 / network failure.
 *
 * Admin-route role-check lives in `_authenticated/admin.tsx` and runs AFTER
 * this guard, so by the time it inspects `user.role` the bootstrap has
 * already populated (or stubbed) the user object — see decisions-log D-03.
 *
 * Story E01.11: wrap children in `<Shell />` so every authenticated page
 * gets header + sidebar + content area without each route re-implementing
 * the chrome.
 */
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: ({ location }) => bootstrapAuth(location.pathname),
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  return (
    <Shell>
      <Outlet />
    </Shell>
  )
}
